import { TextField } from "@shared/components/TextField";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { getStringRequiredFieldMessage } from "@shared/utils/yup-extensions";
import { Form } from "@shared/components/Form";
import { useNavigate } from "react-router";
import { ActionsContainer } from "../styles";
import { SubscriptionSubmitButton } from "@features/Subscription/components/styles";
import { SubscriptionBackButton } from "@features/Subscription/components/SubscriptionBackButton";
import { useStartCredentialBasedSubscriptionFlow } from "@queries/subscription-flow/useStartCredentialBasedSubscriptionFlow";
import { SubscriptionFlowFieldDto } from "@shared/apis/sidekick/generated";
import { toast } from "@shared/services/toast";

interface Props {
  planId: string;
  planName: string;
  stepStartChallenge: SubscriptionFlowFieldDto[] | null;
}

export const StartCredentialBasedMlsSubscription = ({
  planId,
  planName,
  stepStartChallenge,
}: Props) => {
  const fields = stepStartChallenge ?? [];

  const schema = yup
    .object(
      Object.fromEntries(
        fields.map((field) => [
          field.name,
          yup
            .string()
            .trim()
            .min(1, getStringRequiredFieldMessage)
            .required()
            .label(field.label),
        ]),
      ),
    )
    .required();

  type Schema = Record<string, string>;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<Schema>({
    defaultValues: Object.fromEntries(fields.map((f) => [f.name, ""])),
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
  });

  const navigate = useNavigate();

  const mutation = useStartCredentialBasedSubscriptionFlow(planId, {
    planName,
    onSuccess: (data) => {
      if (!data?.id) {
        console.error("No subscription flow ID returned from the API.");
        toast.error("An unexpected error occurred. Please try again later.");
        return;
      }
      navigate(`/subscribe/${data.id}`);
    },
  });

  const onSubmit = async (data: Schema) => {
    mutation.mutate(data);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} aria-disabled={false}>
      <SubscriptionBackButton
        onClick={() => navigate("/subscribe/mls")}
        disabled={mutation.isPending}
      />
      {fields.map((field) => (
        <TextField
          key={field.name}
          label={field.label}
          name={field.name}
          type={field.type}
          defaultValue=""
          inputProps={{ ...register(field.name) }}
          fieldError={errors[field.name]}
        />
      ))}
      <ActionsContainer>
        <SubscriptionSubmitButton
          isLoading={mutation.isPending}
          loadingText="Processing..."
        >
          Next
        </SubscriptionSubmitButton>
      </ActionsContainer>
    </Form>
  );
};
